import { FC } from "react";
import { P1 } from "../atoms/Paragraph";
import { HeadingSize, H2 } from "../atoms/Heading";
import styled from "styled-components";
import { color as themeColor } from "@styles";
import { StandardSection } from "../templates/StandardSection";

interface Props {
  id?: string;
  comparison: string[][];
  pt?: string;
  pb?: string;
}

export const ComparisonTable: FC<React.PropsWithChildren<Props>> = ({ id, comparison, pt, pb }) => {
  return (
    <Container id={id} aria-label="Comparison Table">
      <StandardSection>
        <GridRow>
          <MainHeading marginRight="2rem">
            <H2 size={HeadingSize.H1} align="left" fontSize="3.5rem" lineheight="4rem">
              Invest in insurance that your employees will use and value every day.
            </H2>
          </MainHeading>
          <MarginLeftDiv>
            <P1>The YuLife model is centred around risk prevention, not claims compensation.</P1>
            <P1>We use technology to motivate and inspire people to live healthier lives.</P1>
          </MarginLeftDiv>
        </GridRow>
        <div>
          <Table>
            <thead>
              <tr>
                <BeforeTH>
                  <StyledP1>
                    <Strong>The Old Way</Strong>
                  </StyledP1>
                  <StyledP1>Traditional group life insurance</StyledP1>
                </BeforeTH>
                <AfterTH>
                  <StyledP1>
                    <Strong>The New Way</Strong>
                  </StyledP1>
                  <StyledP1>YuLife group life insurance</StyledP1>
                </AfterTH>
              </tr>
            </thead>
            <tbody>
              {!comparison.length
                ? null
                : comparison.map(([before, after], index) => (
                    <tr key={`${before}-${after}`}>
                      <BeforeTD lastRow={index === comparison.length - 1}>{before}</BeforeTD>
                      <AfterTD lastRow={index === comparison.length - 1}>{after}</AfterTD>
                    </tr>
                  ))}
            </tbody>
          </Table>
        </div>
      </StandardSection>
    </Container>
  );
};

const Container = styled.section`
  background: linear-gradient(#f4f0ff 69.1%, #fff 69.1%);
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 5rem;

  @media only screen and (max-width: 52rem) {
    margin-bottom: 1.5rem;
    display: block;
  }
`;

const MainHeading = styled.div<{ marginRight?: string }>`
  ${(p) => (p.marginRight ? `margin-right: ${p.marginRight}` : null)};

  @media only screen and (max-width: 52rem) {
    margin-right: 0;
  }
`;

const MarginLeftDiv = styled.div`
  margin-left: 6rem;

  @media only screen and (max-width: 52rem) {
    margin-left: 0;
  }
`;

const customTextSelection = `
  ::-moz-selection {
    background: ${themeColor.P20};
  }
  ::selection {
    background: ${themeColor.P20};
  }
`;

const StyledP1 = styled(P1)`
  margin: 0;
  padding: 0;
  font-size: 1.1rem;
  ${customTextSelection};
  color: inherit;
  text-align: left;
`;

const Strong = styled.strong`
  display: block;
  letter-spacing: 0.075rem;
  margin: 0;
  padding: 0;
  font-size: 1.1rem;
  ${customTextSelection};
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  margin: 0 auto;
  padding: 0;
  border: 0.125rem solid #c7b4fd;
  box-shadow: 0.5rem 0.5rem 0px #eae1ff;
  border-radius: 0.5rem;

  td,
  th {
    vertical-align: top;
    padding: 1rem 0;
    line-height: 2rem;
    letter-spacing: 0.075rem;
    font-weight: 400;
    text-decoration: inherit;
    padding: 1.5rem 2rem;
    font-size: 1.1rem;
    ${customTextSelection};

    @media only screen and (max-width: 60rem) {
      padding: 1rem 0.75rem;
      font-size: 0.95rem;
    }
  }

  td:first-of-type {
    border-left: 0.125rem solid #c7b4fd;
  }

  td:last-of-type {
    border-right: 0.125rem solid #c7b4fd;
  }

  th {
    font-weight: bold;
    border-top: 0.125rem solid #c7b4fd;
    border-right: 0.125rem solid #eae1ff;
  }

  tr:nth-child(odd) {
    background-color: #fafafe;
  }

  tr:nth-child(even) {
    background-color: #fff;
  }
`;

const BeforeTD = styled.td<{ lastRow?: boolean }>`
  background: #f5f5f5;
  color: #a0a09b;
  border: 0.125rem solid #eae1ff;
  border-left: none;
  border-bottom: ${(p) => (p.lastRow ? "0.125rem solid #c7b4fd" : "none")};
`;

const AfterTD = styled.td<{ lastRow?: boolean }>`
  color: #956aff;
  border-top: 0.125rem solid #eae1ff;
  border-bottom: ${(p) => (p.lastRow ? "0.125rem solid #c7b4fd" : "none")};
`;

const BeforeTH = styled.th`
  background: #f5f5f5;
  color: #a0a09b;
`;

const AfterTH = styled.td`
  background: #fff;
  color: #956aff;
`;
